// Sparkmage shock helpers. Pure. No React, no Three.js.
//
// ── Codex (Sparkmage) ──
//   "Tap → Target nearby location. Deal damage to another random unit
//    there equal to the sum of (A) on spells you've cast this turn."
//
// The orchestrator (GameBoard / avatarAbilities) owns the cast-history
// bookkeeping, the cell-pick prompt, and the damage application. This
// module owns the (A)-sum arithmetic and the random-unit pick.
//
// Catalog shape is the NORMALIZED card object emitted by
// `normalizeSorceryCard` (airThreshold is a top-level number), same
// contract as the Flamecaller helpers.

import { countsAsUnit } from '../../../core/typeline';

function lookupCatalog(cardId, catalog) {
  if (!cardId || !catalog) return null;
  return catalog.find((c) => c?.unique_id === cardId) || null;
}

/** The card's printed Air threshold (its (A) value for the sum). */
export function airThreshold(cardId, catalog) {
  const full = lookupCatalog(cardId, catalog);
  return Number(full?.airThreshold || 0);
}

/** Sum of (A) thresholds across the cast-history cardIds. */
export function sumAirThresholds(cardIds, catalog) {
  let sum = 0;
  for (const id of cardIds || []) sum += airThreshold(id, catalog);
  return sum;
}

/**
 * Pick one unit at random from `units`, excluding the source card
 * ("another random unit"). `rand` is injectable for tests; defaults
 * to Math.random.
 *
 * @returns the picked card instance, or null when no eligible unit.
 */
export function pickRandomOtherUnit(units, sourceId, rand = Math.random) {
  const eligible = (units || []).filter(
    (u) => u && u.id !== sourceId && !u.isSite && countsAsUnit(u),
  );
  if (eligible.length === 0) return null;
  return eligible[Math.floor(rand() * eligible.length) % eligible.length];
}

/**
 * The 9 cells of a "nearby location" target (source cell + 8
 * surrounding), clipped to the grid. Returns `Set<"col,row">`.
 */
export function nearbyCells(sourceCell, totalCols, totalRows) {
  const out = new Set();
  if (!sourceCell) return out;
  for (let dc = -1; dc <= 1; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      const col = sourceCell.col + dc;
      const row = sourceCell.row + dr;
      if (col < 0 || row < 0 || col >= totalCols || row >= totalRows) continue;
      out.add(`${col},${row}`);
    }
  }
  return out;
}
